import { Injectable } from '@angular/core';
import { ChatRole } from '@app/enums';
import { ConversationMessage } from '../realtime-session-stream/conversation-message.interface';
import { ConversationStateService } from '../conversation-state/conversation-state.service';
import { FeedbackStateManagerService } from '../realtime-session-stream/feedback-state-manager.service';
import { ConversationHistoryStorageService } from '../conversation-history/conversation-history-storage.service';
import { FeedbackSentiment, FeedbackCategory } from '../realtime-session-stream/feedback-state.interface';

/**
 * Service responsible for message operations (create, update, delete).
 * Delegates state updates to ConversationStateService.
 */
@Injectable({
    providedIn: 'root'
})
export class MessageOperationsService {
    constructor(
        private readonly conversationState: ConversationStateService,
        private readonly feedbackStateManager: FeedbackStateManagerService,
        private readonly conversationHistoryStorage: ConversationHistoryStorageService
    ) {}

    /**
     * Generate a unique request ID
     */
    generateRequestId(): string {
        return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Generate a unique message ID
     */
    generateMessageId(): string {
        return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    /**
     * Add a user message to the conversation
     */
    addUserMessage(content: string): ConversationMessage {
        // Remove feedback prompts when adding new user message
        this.conversationState.removeMessagesByType('feedback_prompt');

        const message: ConversationMessage = {
            content,
            role: ChatRole.User,
            requestId: this.generateRequestId(),
            messageId: this.generateMessageId(),
            messageType: 'user',
            timestamp: Date.now()
        };

        this.conversationState.addMessage(message);
        // console.log('💬 User message added');

        return message;
    }

    /**
     * Create an assistant message placeholder for streaming
     */
    createAssistantPlaceholder(requestId: string): ConversationMessage {
        const sessionId = this.conversationHistoryStorage.getCurrentConversationId();

        const message: ConversationMessage = {
            content: '', // Starts empty
            role: ChatRole.Assistant,
            requestId,
            messageId: this.generateMessageId(),
            messageType: 'assistant',
            timestamp: Date.now(),
            sessionId: sessionId.toString(),
            streamingState: {
                isStreaming: true,
                isComplete: false,
                startTime: Date.now()
            },
            datasheetState: {
                status: 'idle',
                items: []
            },
            streamingComplete: false // Backward compatibility
        };

        this.conversationState.addMessage(message);
        const newIndex = this.conversationState.messages.length - 1;
        this.conversationState.setStreamingState(true, newIndex);

        return message;
    }

    /**
     * Update streaming message content
     */
    updateStreamingMessageContent(token: string): void {
        const index = this.conversationState.streamingMessageIndex;

        if (index === undefined || index < 0) {
            return;
        }

        const message = this.conversationState.messages[index];
        if (!message || message.role !== ChatRole.Assistant) {
            return;
        }

        const newContent = (message.content || '') + token;

        this.conversationState.updateMessageByIndex(index, {
            content: newContent,
            streamingState: {
                isStreaming: true,
                isComplete: false,
                ...message.streamingState,
                lastUpdate: Date.now()
            }
        });

        // Update current streaming text
        this.conversationState.updateState({
            currentStreamingText: newContent
        });
    }

    /**
     * Mark streaming message as complete
     */
    completeStreamingMessage(): void {
        const index = this.conversationState.streamingMessageIndex;
        if (index === undefined || index < 0) return;

        const message = this.conversationState.messages[index];
        if (!message) return;

        this.conversationState.updateMessageByIndex(index, {
            streamingState: {
                ...message.streamingState, // PRESERVE existing streamingState properties (including custom ones like noDocumentsRetrieved)
                isStreaming: false,
                isComplete: true,
                startTime: message.streamingState?.startTime || Date.now(),
                endTime: Date.now(),
                duration: Date.now() - (message.streamingState?.startTime || Date.now())
            },
            streamingComplete: true // Backward compatibility
        });

        this.conversationState.completeStreaming();
        // console.log('✅ Marked assistant message as complete');
    }

    /**
     * Mark streaming message as error
     * Sets error flag to prevent feedback prompt from showing
     */
    markStreamingMessageAsError(errorMessage: string): void {
        const index = this.conversationState.streamingMessageIndex;
        if (index === undefined || index < 0) return;

        const message = this.conversationState.messages[index];
        if (!message) return;

        this.conversationState.updateMessageByIndex(index, {
            streamingState: {
                ...message.streamingState,
                isStreaming: false,
                isComplete: true,
                error: errorMessage, // Mark as error - prevents feedback prompt
                startTime: message.streamingState?.startTime || Date.now(),
                endTime: Date.now(),
                duration: Date.now() - (message.streamingState?.startTime || Date.now())
            },
            streamingComplete: true // Backward compatibility
        });

        this.conversationState.completeStreaming();
        console.log('❌ Marked assistant message as error - no feedback prompt will show');
    }

    /**
     * Add an assistant message (non-streaming)
     */
    addAssistantMessage(content: string, requestId?: string): ConversationMessage {
        const message: ConversationMessage = {
            content,
            role: ChatRole.Assistant,
            requestId: requestId || this.generateRequestId(),
            messageId: this.generateMessageId(),
            messageType: 'assistant',
            timestamp: Date.now()
        };

        this.conversationState.addMessage(message);
        return message;
    }

    /**
     * Add feedback prompt for the last assistant message
     */
    addFeedbackPrompt(targetRequestId: string): ConversationMessage | null {
        const messages = this.conversationState.messages;

        // Find assistant messages
        const assistantMessages = messages.filter(
            (msg) => msg.role === ChatRole.Assistant && msg.messageType === 'assistant'
        );

        if (assistantMessages.length === 0) {
            // console.log('🚫 No assistant messages found');
            return null;
        }

        const lastAssistant = assistantMessages[assistantMessages.length - 1];

        // Only add feedback for the last assistant message
        if (lastAssistant.requestId !== targetRequestId) {
            // console.log('🚫 Target is not the last assistant message');
            return null;
        }

        // Remove existing feedback prompts
        this.conversationState.removeMessagesByType('feedback_prompt');

        // Initialize feedback state
        const conversationId = this.conversationHistoryStorage.getCurrentConversationId().toString();
        const messageId = lastAssistant.messageId || this.generateMessageId();
        const feedbackState = this.feedbackStateManager.initializeFeedback(conversationId, messageId, targetRequestId);

        const feedbackMessage: ConversationMessage = {
            content: 'Is this a good response for the question?',
            role: ChatRole.Assistant,
            requestId: `feedback_prompt_${targetRequestId}`,
            messageId: this.generateMessageId(),
            messageType: 'feedback_prompt',
            timestamp: Date.now(),
            feedbackData: {
                targetRequestId,
                isSubmitted: false
            },
            feedbackState: {
                feedback_id: feedbackState.feedback_id,
                status: feedbackState.status,
                prompted_at: feedbackState.prompted_at
            }
        };

        this.conversationState.addMessage(feedbackMessage);
        // console.log('✅ Added feedback prompt');

        return feedbackMessage;
    }

    /**
     * Mark feedback as submitted
     */
    markFeedbackSubmitted(
        targetRequestId: string,
        sentiment?: FeedbackSentiment,
        data?: {
            categories?: FeedbackCategory[];
            reasons?: string[];
            comment?: string;
            userEmail?: string;
        }
    ): void {
        const messages = this.conversationState.messages;
        const feedbackIndex = messages.findIndex(
            (msg) => msg.messageType === 'feedback_prompt' && msg.feedbackData?.targetRequestId === targetRequestId
        );

        if (feedbackIndex === -1) return;

        const feedbackMessage = messages[feedbackIndex];

        // Submit through feedback manager if sentiment provided
        if (sentiment && feedbackMessage.feedbackState) {
            this.feedbackStateManager.submitFeedback(
                {
                    feedback_id: feedbackMessage.feedbackState.feedback_id,
                    message_id: feedbackMessage.messageId,
                    request_id: targetRequestId,
                    status: 'pending',
                    prompted_at: feedbackMessage.feedbackState.prompted_at
                },
                sentiment,
                data || {}
            );
        }

        // Update the feedback message
        this.conversationState.updateMessageByIndex(feedbackIndex, {
            content: 'Feedback received, thanks for your help!',
            feedbackData: {
                ...feedbackMessage.feedbackData!,
                isSubmitted: true,
                response: sentiment,
                timestamp: Date.now()
            },
            feedbackState: feedbackMessage.feedbackState
                ? {
                      ...feedbackMessage.feedbackState,
                      status: 'submitted' as const,
                      sentiment,
                      categories: data?.categories,
                      reasons: data?.reasons,
                      comment: data?.comment,
                      submitted_at: Date.now()
                  }
                : undefined
        });

        // Save feedback state
        const conversationId = this.conversationHistoryStorage.getCurrentConversationId().toString();
        this.feedbackStateManager.saveFeedbackState(conversationId);

        // console.log('✅ Feedback marked as submitted');
    }

    /**
     * Update last message's cognitive state
     */
    updateLastMessageCognitiveState(cognitiveState: any): void {
        const messages = this.conversationState.messages;
        if (messages.length === 0) return;

        const lastIndex = messages.length - 1;
        const lastMessage = messages[lastIndex];

        if (lastMessage.role !== ChatRole.Assistant) {
            return;
        }

        this.conversationState.updateMessageByIndex(lastIndex, {
            cognitiveState: cognitiveState
        });
    }
}
